import type { Account, Workflow, Recipe, StepName, ConfigFile } from './types'
import { importWorkflowJson } from './io'
import basic3vSample from './data/samples/aus-basic3v.json'
import advanced2vSample from './data/samples/aus-advanced2v.json'
import advanced2vV3Sample from './data/samples/aus-advanced2v-v3.json'

export const IDV_PROVIDERS = ['daon', 'incode', 'onfido'] as const
export const KYC_PROVIDERS = ['dvs', 'experian', 'equifax', 'superchoice', 'manual'] as const
export const AML_SOURCES = ['refinitiv_peps', 'refinitiv_sanctions', 'refinitiv_adverse_media', 'dowjones'] as const
export const IDV_ATTESTATIONS = ['DOCUMENT', 'FACIAL_COMPARISON', 'FACIAL_LIVENESS', 'OCR_COMPARISON'] as const

// Related config files per node type (mock content)
export const RELATED_CONFIG_FILES: Record<StepName | 'WEBHOOK', string[]> = {
  IDV: ['idv_services', 'onesdk_utility', 'con_daon'],
  KYC: ['con_dvs', 'kyc_ruleset'],
  AML: ['con_refinitiv', 'aml_config'],
  KYB: ['con_dnb', 'kyb_config'],
  FRAUD: ['fraud_config', 'con_seon'],
  MATCHLIST: ['matchlist_config'],
  DECISION: ['decision_rules'],
  TOGGLEMONITORING: ['monitoring_config'],
  WEBHOOK: ['webhook_config'],
}

export function defaultConfigFiles(names: string[]): ConfigFile[] {
  return names.map(name => ({ name, content: `# ${name}\n# mock config — edit inline\n` }))
}

const SEED_ACCOUNT_ID = 'acc_demo'
const SEED_NOW = new Date().toISOString()

// Build the two bundled sample workflows with stable ids so they seed the demo
// account on first run (before any persisted state exists).
function seedWorkflow(raw: unknown, id: string): Workflow {
  return { ...importWorkflowJson(raw, SEED_ACCOUNT_ID), id, createdAt: SEED_NOW, updatedAt: SEED_NOW }
}

export const SEED_WORKFLOWS: Workflow[] = [
  seedWorkflow(advanced2vV3Sample, 'wf_sample_advanced2v_v3'),
  seedWorkflow(advanced2vSample, 'wf_sample_advanced2v'),
  seedWorkflow(basic3vSample, 'wf_sample_basic3v'),
]

// Seed v1 recipes (entity_profiles). Safe Harbour ID (pep) and its media variant
// (pep_media adds adverse media).
export const SEED_RECIPES: Recipe[] = [
  {
    id: 'recipe_safe_harbour_id',
    name: 'safe_harbour_id',
    longName: 'Safe Harbour & ID',
    checkType: ['blacklist', 'two_plus', 'id', 'pep', 'idvalidate'],
    checkOrder: 'blacklist, !id, ent, idv, aml',
    mkyc: false,
    createdAt: SEED_NOW,
    updatedAt: SEED_NOW,
  },
  {
    id: 'recipe_safe_harbour_id_media',
    name: 'safe_harbour_id_media',
    longName: 'Safe Harbour & ID + Media',
    checkType: ['blacklist', 'two_plus', 'id', 'pep_media', 'idvalidate'],
    checkOrder: 'blacklist, !id, ent, idv, aml',
    mkyc: false,
    createdAt: SEED_NOW,
    updatedAt: SEED_NOW,
  },
]

// Seed one demo account containing the two sample workflows.
export const SEED_ACCOUNTS: Account[] = [
  { id: SEED_ACCOUNT_ID, name: 'Demo Customer (OwnHome)', description: 'Sample account',
    workflowIds: SEED_WORKFLOWS.map(w => w.id), createdAt: SEED_NOW, updatedAt: SEED_NOW },
]
